import React, { useState, useMemo } from 'react';
import Paper from '@material-ui/core/Paper';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core';
import { omit } from 'lodash';
import FormHelperText from '@material-ui/core/FormHelperText';

import AdvancedOptionsDialog from './AdvancedOptionsDialog';

const useStyles = makeStyles({
  tableWrapper: {
    maxHeight: '60vh',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  checkboxContainer: {
    display: 'flex',
    minWidth: '175px',
    alignItems: 'center'
  },
  checkbox: {
    padding: '0'
  },
  button: {
    padding: '0'
  }
});

const OptionsGrid = props => {
  const classes = useStyles();
  const [showAdvancedDialog, setShowAdvancedDialog] = useState(false);
  const [currentOption, setCurrentOption] = useState();

  const { choices, formprops, meta } = props;
  const { error, touched } = meta;
  const {
    values: { options = [] }
  } = formprops;

  const { selectedAllContact = [], selectedCcContacts = [] } = useMemo(() => {
    const selectedAllContact = [];
    const selectedCcContacts = [];
    options.forEach(option => {
      if (option.includeAllPrimary) {
        selectedAllContact.push(option.fundId);
      }
      if (option.includeAllWithCC) {
        selectedCcContacts.push(option.fundId);
      }
    });
    return { selectedAllContact, selectedCcContacts };
  }, [options]);

  /**
   * Handle checkbox select includeAllPrimary for the passed fund
   * @param {*} fundId
   */
  const handleAllContactSelect = fundId => {
    const array = [...options];
    const index = array.findIndex(obj => obj.fundId === fundId);

    if (index >= 0) {
      if (array[index].includeAllPrimary && !array[index].includeAllWithCC) {
        // remove option from the list when both contact and contactCc become unselected
        array.splice(index, 1);
      } else {
        array[index] = {
          ...array[index],
          includeAllPrimary: !array[index].includeAllPrimary
        };
      }
    } else {
      array.push({
        fundId,
        includeAllPrimary: true,
        includeUsersWhoHaveNotLoggedIn: true,
        includeLoggedInUsers: true,
        includeInactiveInvestors: true,
        includeAdministrator: false,
        includeUsersWhoDidNotAccessDocument: false
      });
    }

    formprops.form.change('options', array);
  };

  /**
   * Handle checkbox select includeAllPrimary for ALL funds
   */
  const handleAllContactsSelectAll = () => {
    let array = [...options];
    if (selectedAllContact.length < choices.length) {
      choices.forEach(choice => {
        const index = array.findIndex(obj => obj.fundId === choice.id);
        if (index >= 0) {
          array[index] = {
            ...array[index],
            includeAllPrimary: true
          };
        } else {
          array.push({
            fundId: choice.id,
            includeAllPrimary: true,
            includeUsersWhoHaveNotLoggedIn: true,
            includeLoggedInUsers: true,
            includeInactiveInvestors: true,
            includeAdministrator: false,
            includeUsersWhoDidNotAccessDocument: false
          });
        }
      });
    } else {
      // remove options from the list when both contact and contactCc become unselected
      array = array.filter(option => option.includeAllWithCC);
      array.forEach((option, index) => {
        array[index] = {
          ...option,
          includeAllPrimary: false
        };
      });
    }

    formprops.form.change('options', array);
  };

  /**
   * Handle checkbox select includeAllWithCC for the passed fund
   * @param {*} fundId
   */
  const handleCcContactSelect = fundId => {
    const array = [...options];
    const index = array.findIndex(obj => obj.fundId === fundId);

    if (index >= 0) {
      if (!array[index].includeAllPrimary && array[index].includeAllWithCC) {
        // remove option from the list when both contact and contactCc become unselected
        array.splice(index, 1);
      } else {
        array[index] = {
          ...array[index],
          includeAllWithCC: !array[index].includeAllWithCC
        };
      }
    } else {
      array.push({
        fundId,
        includeAllWithCC: true,
        includeUsersWhoHaveNotLoggedIn: true,
        includeLoggedInUsers: true,
        includeInactiveInvestors: true,
        includeAdministrator: false,
        includeUsersWhoDidNotAccessDocument: false
      });
    }
    formprops.form.change('options', array);
  };

  /**
   * Handle checkbox select includeAllWithCC for ALL funds
   */
  const handleCcContactsSelectAll = () => {
    let array = [...options];
    if (selectedCcContacts.length < choices.length) {
      choices.forEach(choice => {
        const index = array.findIndex(obj => obj.fundId === choice.id);
        if (index >= 0) {
          array[index] = {
            ...array[index],
            includeAllWithCC: true
          };
        } else {
          array.push({
            fundId: choice.id,
            includeAllWithCC: true,
            includeUsersWhoHaveNotLoggedIn: true,
            includeLoggedInUsers: true,
            includeInactiveInvestors: true,
            includeAdministrator: false,
            includeUsersWhoDidNotAccessDocument: false
          });
        }
      });
    } else {
      // remove options from the list when both contact and contactCc become unselected
      array = array.filter(option => option.includeAllPrimary);
      array.forEach((option, index) => {
        array[index] = {
          ...option,
          includeAllWithCC: false
        };
      });
    }

    formprops.form.change('options', array);
  };

  const handleAdvancedDialogToggle = toggle => {
    setShowAdvancedDialog(toggle);
  };

  const handleAdvancedClick = fundId => {
    const option = options.find(obj => obj.fundId === fundId);

    setCurrentOption(
      option || {
        fundId,
        includeUsersWhoHaveNotLoggedIn: true,
        includeLoggedInUsers: true,
        includeInactiveInvestors: true,
        includeAdministrator: false,
        includeUsersWhoDidNotAccessDocument: false
      }
    );
    setShowAdvancedDialog(true);
  };

  const handleSetAdvancedOptions = advancedOptions => {
    const array = [...options];

    if (advancedOptions.applyToAll) {
      const anonAdvancedOptions = omit({ ...advancedOptions }, [
        'fundId',
        'includeAllPrimary',
        'includeAllWithCC',
        'applyToAll'
      ]);
      choices.forEach(choice => {
        const index = array.findIndex(obj => obj.fundId === choice.id);
        if (index >= 0) {
          array[index] = {
            ...array[index],
            ...anonAdvancedOptions
          };
        } else {
          array.push({
            fundId: choice.id,
            ...anonAdvancedOptions
          });
        }
      });
    } else {
      const anonAdvancedOptions = omit({ ...advancedOptions }, ['applyToAll']);
      const index = array.findIndex(
        obj => obj.fundId === advancedOptions.fundId
      );
      if (index >= 0) {
        array[index] = {
          ...array[index],
          ...anonAdvancedOptions
        };
      } else {
        array.push({
          ...anonAdvancedOptions
        });
      }
    }

    formprops.form.change('options', array);
  };

  return (
    <div className={classes.tableWrapper}>
      <Paper className={classes.paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Fund</TableCell>
              <TableCell padding="checkbox">
                <div className={classes.checkboxContainer}>
                  <Checkbox
                    className={classes.checkbox}
                    indeterminate={
                      selectedAllContact.length > 0 &&
                      selectedAllContact.length < choices.length
                    }
                    checked={
                      selectedAllContact.length > 0 &&
                      selectedAllContact.length === choices.length
                    }
                    onChange={handleAllContactsSelectAll}
                  />
                  Include All Contacts
                </div>
              </TableCell>
              <TableCell padding="checkbox">
                <div className={classes.checkboxContainer}>
                  <Checkbox
                    className={classes.checkbox}
                    indeterminate={
                      selectedCcContacts.length > 0 &&
                      selectedCcContacts.length < choices.length
                    }
                    checked={
                      selectedCcContacts.length > 0 &&
                      selectedCcContacts.length === choices.length
                    }
                    onChange={handleCcContactsSelectAll}
                  />
                  Include CC Contacts
                </div>
              </TableCell>
              <TableCell align="right">Advanced Options</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {choices.map(choice => {
              return (
                <TableRow key={choice.id}>
                  <TableCell>{choice.name}</TableCell>
                  <TableCell className={classes.checkboxCell} align="center">
                    <Checkbox
                      className={classes.checkbox}
                      checked={selectedAllContact.includes(choice.id)}
                      onClick={() => handleAllContactSelect(choice.id)}
                    />
                  </TableCell>
                  <TableCell className={classes.checkboxCell} align="center">
                    <Checkbox
                      className={classes.checkbox}
                      checked={selectedCcContacts.includes(choice.id)}
                      onClick={() => handleCcContactSelect(choice.id)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      color="primary"
                      className={classes.button}
                      onClick={() => handleAdvancedClick(choice.id)}
                    >
                      Advanced
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      {!!(touched && error) && <FormHelperText error>{error}</FormHelperText>}
      <AdvancedOptionsDialog
        option={currentOption}
        showDialog={showAdvancedDialog}
        handleClose={() => handleAdvancedDialogToggle(false)}
        handleSetAdvancedOptions={handleSetAdvancedOptions}
      />
    </div>
  );
};

export default OptionsGrid;
